import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { plainToInstance } from "class-transformer";
import * as _ from "lodash";
import { lastValueFrom, Observable } from "rxjs";
import { API_ASSOCIATIONS } from "src/common/constants/api.constants";
import { TAPIListResult, TAPIQuery } from "src/common/contracts/api.contracts";
import { apiCallWrapper, apiSubscriptionWrapper } from "../../api/api.util";
import { AuditApi } from "../../api/audit/audit.api";
import { HasIdOrExternalValueOrExternalIdOrServiceName } from "../../api/generic.api";
import { ALERT_TYPE_DESCRIPTION } from "../../model/alert/contract/alert-contract";
import { Audit } from "../../model/audit/audit.model";
import {
  AuditActionTypeEnum,
  AuditFlattenedValueKeysEnum,
  AuditModelTypeEnum,
} from "../../model/audit/contract/audit-contract";
import { BRANDING_TYPE_DESCRIPTION } from "../../model/forms/contract/form-template-contract";
import { IQueryFilter } from "../../model/query.filter.class";
import { AuthService } from "../auth/auth.service";
import { BaseService } from "../base.service";
import { EmailTemplateEnum } from "../email/contract/email.contract";
import { RoleService } from "../role/role.service";
import { ToastService } from "../toast.service";
import { USER_TYPE } from "../user/contract/user-type.contract";
import { SocketData } from "../web-socket/contract/web-socket.contract";
import { WorkflowStepService } from "../workflow/workflow-step.service";

@Injectable({
  providedIn: "root",
})
export class AuditService extends BaseService {
  translations: Array<{
    key: string[];
    auditObjectTypeId?: AuditModelTypeEnum; // Optional model type to further filter the translation applicability
    fixedMapping?: { sourceValue: string | number; returnValue: string }[];
    returnValueMappingFunction?: (value: string | number | undefined) => string | Promise<string>;
  }> = [];

  constructor(
    private toastService: ToastService,
    private auditApi: AuditApi,
    private workflowStepService: WorkflowStepService,
    private roleService: RoleService,
    private authService: AuthService,
    private router: Router
  ) {
    super();

    // Initialize translations - these are used to convert raw audit values into user friendly strings
    this.translations.push(
      {
        key: [AuditFlattenedValueKeysEnum.BrandingType],
        returnValueMappingFunction: async (value) => {
          return (
            BRANDING_TYPE_DESCRIPTION[value as keyof typeof BRANDING_TYPE_DESCRIPTION] || "Unknown"
          );
        },
      },
      {
        key: [
          AuditFlattenedValueKeysEnum.WorkflowStepCurrent,
          AuditFlattenedValueKeysEnum.WorkflowStepId,
        ],
        returnValueMappingFunction: async (value) => {
          return (
            (await this.workflowStepService.getWorkflowSteps()).find((ws) => ws.id === value)
              ?.name || "Unknown"
          );
        },
      },
      {
        key: [AuditFlattenedValueKeysEnum.WorkflowStepPrevious],
        returnValueMappingFunction: async (value) => {
          return (
            (await this.workflowStepService.getWorkflowSteps()).find((ws) => ws.id === value)
              ?.name || "Unknown"
          );
        },
      },
      {
        key: [AuditFlattenedValueKeysEnum.EmailTemplateId],
        returnValueMappingFunction: async (value) => {
          return EmailTemplateEnum[value as keyof typeof EmailTemplateEnum];
        },
      },
      {
        key: [AuditFlattenedValueKeysEnum.UserType],
        returnValueMappingFunction: async (value) => {
          return USER_TYPE[value as keyof typeof USER_TYPE] || "Unknown";
        },
      },
      {
        key: [AuditFlattenedValueKeysEnum.AlertType],
        auditObjectTypeId: AuditModelTypeEnum.Alert,
        returnValueMappingFunction: async (value: string | number | undefined) => {
          if (value === undefined) return "Unknown";
          return ALERT_TYPE_DESCRIPTION[value as keyof typeof ALERT_TYPE_DESCRIPTION] || "Unknown";
        },
      },
      {
        key: [AuditFlattenedValueKeysEnum.UserRole],
        returnValueMappingFunction: async (value) => {
          return (await this.roleService.getRoles()).find((r) => r.id === value)?.name || "Unknown";
        },
      },
      {
        key: [
          AuditFlattenedValueKeysEnum.IsApproved,
          AuditFlattenedValueKeysEnum.IsArhived,
          AuditFlattenedValueKeysEnum.IsAvailableOffline,
          AuditFlattenedValueKeysEnum.IsInChangesRequired,
          AuditFlattenedValueKeysEnum.IsClosed,
          AuditFlattenedValueKeysEnum.IsComplete,
          AuditFlattenedValueKeysEnum.IsDeleted,
          AuditFlattenedValueKeysEnum.IsDraft,
          AuditFlattenedValueKeysEnum.IsInReview,
          AuditFlattenedValueKeysEnum.IsOnline,
          AuditFlattenedValueKeysEnum.IsPracticeOnly,
          AuditFlattenedValueKeysEnum.IsPublished,
          AuditFlattenedValueKeysEnum.IsReportSent,
          AuditFlattenedValueKeysEnum.IsPreviewData,
          AuditFlattenedValueKeysEnum.IsMSF,
          AuditFlattenedValueKeysEnum.ReadyToSend,
          AuditFlattenedValueKeysEnum.HaveMinimumResponsesNotBeenReceived,
          AuditFlattenedValueKeysEnum.HasSentMinThresholdReachedEmail,
          AuditFlattenedValueKeysEnum.HasSentPerctThresholdReachedEmail,
        ],
        fixedMapping: [
          { sourceValue: 1, returnValue: "Yes" },
          { sourceValue: 0, returnValue: "No" },
        ],
      }
    );
  }

  async createTimeoutAudit(): Promise<boolean> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.auditApi.createTimeout(), {
          toastService: this.toastService,
          action: "Creating timeout audit",
        })
      );
      return data.success;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns a an benchmarkJob by id
   */
  async getAudit(id: number): Promise<Audit> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.auditApi.get(id), {
          toastService: this.toastService,
          action: "Fetch audit by id",
        })
      );

      return plainToInstance(Audit, data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns a paginated list of audits
   */
  async getAudits(
    auditModelType?: AuditModelTypeEnum,
    auditActionType?: AuditActionTypeEnum,
    auditObjectId?: number,
    queryParams?: TAPIQuery
  ): Promise<TAPIListResult<Audit>> {
    try {
      const componentQuery = this.transformQueryParamsToIQueryFilter(queryParams);

      const defaultQuery: IQueryFilter = new IQueryFilter(_.cloneDeep(API_ASSOCIATIONS.audit));

      if (!!auditModelType) {
        defaultQuery.filter = { objectTypeId: auditModelType };
      }

      if (!!auditActionType) {
        defaultQuery.filter = { ...defaultQuery.filter, actionId: auditActionType };
      }

      if (!!auditObjectId) {
        defaultQuery.filter = { ...defaultQuery.filter, objectId: auditObjectId };
      }

      return await this._getAudits(this.mergeQueryFilters(componentQuery, defaultQuery));
    } catch (error) {
      throw error;
    }
  }

  /**
   * @returns a paginated list of all audit logs
   */
  async getAllLogs(
    skip: number = 0,
    limit: number = 20,
    order: string = "asc",
    sortBy: string = "id",
    objectId?: string,
    objectTypeId?: number,
    objectActionTypeId?: number,
    search?: string,
    startDate?: string,
    endDate?: string
  ): Promise<TAPIListResult<Audit>> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(
          this.auditApi.getAllLogs(
            skip,
            limit,
            order,
            sortBy,
            objectId,
            objectTypeId,
            objectActionTypeId,
            search,
            startDate,
            endDate
          ),
          {
            toastService: this.toastService,
            action: "Obtaining Audit Logs",
          }
        )
      );

      return this.inflateModel<
        Audit,
        Audit & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(Audit, data);
    } catch (error) {
      throw error;
    }
  }

  public streamAllAuditLogs(
    order: string = "asc",
    sortBy: string = "id",
    objectId?: string,
    objectTypeId?: number,
    objectActionTypeId?: number,
    search?: string,
    startDate?: string,
    endDate?: string
  ): Observable<SocketData> {
    return apiSubscriptionWrapper(
      this.auditApi.streamAllAuditLogs(
        order,
        sortBy,
        objectId,
        objectTypeId,
        objectActionTypeId,
        search,
        startDate,
        endDate
      ),
      {
        authService: this.authService,
        router: this.router,
        toastService: this.toastService,
        failTitle: "Failed to fetch audit data",
      }
    );
  }

  async getAllUserImpersonationLogs(userImpersonationId: string): Promise<TAPIListResult<Audit>> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.auditApi.getAllUserImpersonationLogs(userImpersonationId), {
          toastService: this.toastService,
          action: "Obtaining User Impersonation Audit Logs",
        })
      );
      return this.inflateModel<
        Audit,
        Audit & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(Audit, data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description The Audit.value contains non-user friendly values for certain model types. This method translates those values into more user friendly strings.
   * @param audit
   */
  async translateAuditModelValue(audit: Audit): Promise<void> {
    const flattenedValue = audit.flattenAuditValue;

    if (!flattenedValue) {
      return;
    }
    await Promise.all(
      Object.keys(flattenedValue).map(async (key) => {
        const translation = this.translations.find((t) => {
          // If translation has a matching key, doesn't have a specific model type or does and that model type matches the audit's model type
          return (
            t.key.includes(key) &&
            (!t.auditObjectTypeId || t.auditObjectTypeId === audit.objectTypeId)
          );
        });

        if (translation) {
          const originalValue = flattenedValue[key];
          let translatedValue = originalValue;
          if (translation.fixedMapping !== undefined) {
            const fixedMappingEntry = translation.fixedMapping.find(
              (mapping) => mapping.sourceValue === originalValue
            );
            if (fixedMappingEntry) {
              translatedValue = fixedMappingEntry.returnValue;
            }
          } else if (translation.returnValueMappingFunction) {
            translatedValue = await translation.returnValueMappingFunction.call(
              this,
              originalValue
            );
          }
          flattenedValue[key] = translatedValue;
        }
      })
    );
  }

  private async _getAudits(query: IQueryFilter): Promise<TAPIListResult<Audit>> {
    try {
      const data = await lastValueFrom(
        apiCallWrapper(this.auditApi.list(query), {
          toastService: this.toastService,
          action: "Obtaining Audits",
        })
      );

      return this.inflateModel<
        Audit,
        Audit & HasIdOrExternalValueOrExternalIdOrServiceName<string>
      >(Audit, data);
    } catch (error) {
      throw error;
    }
  }
}
